// @flow

import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import {
    View, FlatList, Pressable, TextInput, useWindowDimensions,
} from 'react-native';
import { FormattedMessage, useIntl } from 'react-intl';
import { APIContext, Confirmation, withConfirmation } from 'speed-js-react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { navigateWithoutHistory } from 'src/navigation/Stack';
import { commonStyles } from 'src/styles/common';
import { isEmpty } from 'lodash';
import { HOME_SCREEN } from 'src/navigation/constants';
import Title from 'src/components/Title';
import Text from 'src/components/CustomText/CustomText';
import { pageStyles } from 'src/navigation/defaultPageStyles';
import AddedProfiles from 'src/navigation/EditDevice/AddedProfiles';
import DeviceWrapper from 'src/navigation/EditDevice/DeviceWrapper';
import { onDragEnd } from 'src/navigation/Profile/AddedChannelsTable';
import AllProfiles from 'src/navigation/EditDevice/AllProfiles';

type Props = {
    route: Object,
    confirm: (confirmation: Confirmation) => void,
};

const EditDevice = (props: Props) => {
    const { route, confirm } = props;
    const [profiles, setProfiles] = useState([]);
    const [addedProfiles, setAddedProfiles] = useState([]);
    const [hasChange, setHasChange] = React.useState(false);
    const [fetching, setFetching] = React.useState(true);
    const { width } = useWindowDimensions();
    const [name, setName] = useState('');
    const intl = useIntl();
    const { apiClient } = React.useContext(APIContext);
    const [errors, setErrors] = useState(null);
    const navigation = useNavigation();
    const nameErrorRef = React.useRef(null);
    const profilesErrorRef = React.useRef(null);

    React.useEffect(() => {
        document.getElementById('page-description').setAttribute('content', `
        Добавяне и управление на устройства
        `);
    }, []);

    useEffect(() => {
        const accountUUID = localStorage.getItem('account');

        apiClient.resources.devices.get(route.params.uuid)
            .then((device) => {
                setName(device.name);
                apiClient.resources.accountProfiles.list(accountUUID).then((res) => {
                    apiClient.resources.devices.profiles.list(device.uuid).then((deviceProfiles) => {
                        const result1 = res.results.filter((el) => !device.profiles.includes(el.uuid));
                        setProfiles(result1);
                        const result2 = deviceProfiles.results.sort(
                            (a, b) => a.sequence - b.sequence,
                        ).map((el) => el.profile);
                        setAddedProfiles(result2);
                        setFetching(false);
                    });
                });
            });
    }, [route, apiClient.resources.profiles, apiClient.resources]);

    React.useEffect(() => {
        if (!fetching) {
            setHasChange(true);
        }
        return () => {
            setHasChange(false);
        };
        // eslint-disable-next-line
    }, [name, addedProfiles]);
    const renderItem = useCallback(({ item, index }) => (
        <AllProfiles
            item={item}
            index={index}
            addedProfiles={addedProfiles}
            setAddedProfiles={setAddedProfiles}
            setProfiles={setProfiles}
            profiles={profiles}
        />
    ), [addedProfiles, profiles]);

    const handleRequestErrors = React.useCallback((err) => {
        if (err.status === 400) {
            setErrors(err.data);
            if (err.data.name && nameErrorRef.current) {
                // Scroll to name view ref
                nameErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else if (err.data.profiles && profilesErrorRef.current) {
                profilesErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else if (err.data.non_field_errors) {
                setErrors({ name: err.data.non_field_errors });
                if (nameErrorRef.current) {
                    // Scroll to name view ref
                    nameErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }
    }, []);

    const handleSubmit = React.useCallback(() => {
        apiClient.resources.devices.partialUpdate(route.params.uuid, {
            name,
            profiles:
                addedProfiles.map((el) => el.uuid),
        })
            .then(() => {
                navigateWithoutHistory(navigation, HOME_SCREEN, { deviceUpdated: true });
            })
            .catch((err) => handleRequestErrors(err));
    }, [addedProfiles, apiClient.resources.devices, handleRequestErrors, name, navigation, route.params.uuid]);

    const onDeleteHandler = () => {
        confirm({
            message: (
                <FormattedMessage
                    id="delete-device-title"
                    defaultMessage="Are you sure you want to delete the device?"
                />),
            actions: [
                {
                    id: 'cancel',
                    text: <FormattedMessage id="cancel" defaultMessage="Cancel" />,
                    callback: () => {},
                },
                {
                    id: 'confirm',
                    text: <FormattedMessage id="confirm" defaultMessage="Confirm" />,
                    callback: () => {
                        apiClient.resources.devices.delete(route.params?.uuid)
                            .then(() => {
                                navigateWithoutHistory(navigation, HOME_SCREEN, { deviceUpdated: true });
                            });
                    },
                },
            ],
        });
    };

    return (
        <View style={pageStyles.external_wrapper}>
            <DeviceWrapper onDeleteHandler={onDeleteHandler} onSaveHandler={handleSubmit} hasChange={hasChange}>
                <Title
                    text={(
                        <FormattedMessage
                            id="edit-device"
                            defaultMessage="Edit Device"
                        />
                    )}
                />
                <View style={width < 600 && pageStyles.phone_wrapper}>
                    <View style={width > 600 ? pageStyles.wrapper
                        : pageStyles.wrapper_phone}
                    >
                        <View
                            style={width > 900
                                ? pageStyles.inputs_wrapper
                                : pageStyles.inputs_wrapper_phone}
                        >
                            <TextInput
                                onChangeText={(ch) => setName(ch)}
                                onSubmitEditing={handleSubmit}
                                style={width > 600 ? commonStyles.input : commonStyles.input_phone}
                                value={name}
                                placeholder={intl.formatMessage({ id: 'username', defaultMessage: 'Username' })}
                            />
                            <View ref={nameErrorRef}>
                                {errors?.name ? <Text style={pageStyles.text_input_error}>{errors.name}</Text> : null}
                            </View>
                        </View>
                    </View>
                    <View style={pageStyles.added}>
                        <View style={width > 600 ? pageStyles.channels_label : pageStyles.channels_label_phone}>
                            <Text style={width > 600 ? pageStyles.available_channels_label
                                : pageStyles.available_channels_label_phone}
                            >
                                <FormattedMessage
                                    id="added-profiles"
                                    defaultMessage="Added Profiles"
                                />
                            </Text>
                            <Pressable
                                onPress={() => {
                                    setProfiles([...profiles, ...addedProfiles].sort(
                                        (a, b) => (a.number - b.number),
                                    ));
                                    setAddedProfiles([]);
                                }}
                                style={pageStyles.centered}
                            >
                                <Text style={[width > 600 ? pageStyles.light_blue
                                    : [pageStyles.light_blue, pageStyles.centered],
                                width < 600 && pageStyles.up_button]}
                                >
                                    <FormattedMessage
                                        id="remove-all-profiles"
                                        defaultMessage="Remove All Profiles"
                                    />
                                </Text>
                            </Pressable>
                        </View>
                        <View style={pageStyles.channel_label_container}>
                            <View
                                style={width > 600
                                    ? pageStyles.channel_label_first_column
                                    : pageStyles.channel_label_first_column_phone}
                            />
                            <Text style={pageStyles.channel_label_second_column}>
                                <FormattedMessage
                                    id="profile"
                                    defaultMessage="Profile"
                                />
                            </Text>
                        </View>
                        {isEmpty(addedProfiles) && (
                            <View style={pageStyles.empty_row_styles} />
                        )}
                        <View ref={profilesErrorRef}>
                            {errors?.profiles && (
                                <Text style={pageStyles.primary_error}>
                                    {errors.profiles}
                                </Text>
                            )}
                        </View>
                        <DragDropContext onDragEnd={(result) => onDragEnd(result, addedProfiles, setAddedProfiles)}>
                            <Droppable droppableId="profilesList">
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                        {addedProfiles.map((item, index) => (
                                            <AddedProfiles
                                                key={item.uuid}
                                                item={item}
                                                index={index}
                                                addedProfiles={addedProfiles}
                                                setAddedProfiles={setAddedProfiles}
                                                setProfiles={setProfiles}
                                                profiles={profiles}
                                            />
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </View>
                    {profiles.length > 0 && (
                        <View style={width > 600 ? pageStyles.available : pageStyles.added}>
                            <View style={width > 600 ? pageStyles.channels_label : pageStyles.channels_label_phone}>
                                <Text style={width > 600 ? pageStyles.available_channels_label
                                    : pageStyles.available_channels_label_phone}
                                >
                                    <FormattedMessage
                                        id="available-profiles"
                                        defaultMessage="Available Profiles"
                                    />
                                </Text>
                                <Pressable
                                    onPress={() => {
                                        setAddedProfiles([...addedProfiles, ...profiles]);
                                        setProfiles([]);
                                    }}
                                    style={pageStyles.centered}
                                >
                                    <Text style={[width > 600 ? pageStyles.light_blue
                                        : [pageStyles.light_blue, pageStyles.centered],
                                    width < 600 && pageStyles.up_button]}
                                    >
                                        <FormattedMessage id="add-all-profiles" defaultMessage="Add All Profiles" />
                                    </Text>
                                </Pressable>
                            </View>
                            <View style={pageStyles.channel_label_container}>
                                <View
                                    style={width > 600
                                        ? pageStyles.channel_label_first_column
                                        : pageStyles.channel_label_first_column_phone}
                                />
                                <Text style={pageStyles.channel_label_second_column}>
                                    <FormattedMessage
                                        id="profile"
                                        defaultMessage="Profile"
                                    />
                                </Text>
                                <View style={pageStyles.channel_label_fifth_column} />
                            </View>
                            <FlatList
                                showsVerticalScrollIndicator={false}
                                scrollEnabled={width < 960}
                                data={profiles}
                                renderItem={renderItem}
                                keyExtractor={(item) => item.uuid}
                            />
                        </View>
                    )}
                </View>
            </DeviceWrapper>
        </View>
    );
};

export default withConfirmation(EditDevice);
